import React, { useCallback, useMemo, useState } from "react";

import { Product } from "@design-controls/types";
import {
  createQColumnHelper,
  DataProvider,
  QBodyLayout,
  QButton,
  QButtonGroup,
  QDataTable,
  QDeleteConfirmationModal,
  QHeader,
  QText,
  QTitle,
  TableMenuItem,
  Filtering,
} from "@qualio/ui-components";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import { useProducts } from "../../hooks/product/useProducts";
import useHasPermission from "../../hooks/useHasPermission";
import { UserPermissions } from "../../domain/permissions";
import { Link } from "react-router-dom";

const columnHelper = createQColumnHelper<Product>();

export const Products: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = useHasPermission([UserPermissions.MANAGE_PRODUCT]);
  const createProductClick = useCallback(
    () => navigate("/product/create"),
    [navigate],
  );
  const onEditClick = useCallback(
    (product: Product) => navigate(`/product/${product.id}/config`),
    [navigate],
  );
  const {
    products: data,
    isLoading,
    deleteProduct,
    isDeleting,
  } = useProducts();

  const [productToDelete, setProductToBeDelete] = useState<Product | undefined>(
    undefined,
  );

  const columns = useMemo(() => {
    const columns = [
      columnHelper.custom("name", {
        header: t("products.list.table.header.product"),
        cell: (props) => {
          return <Link to={props.row.id}>{props.getValue()}</Link>;
        },
      }),
    ];

    if (!!permissions[UserPermissions.MANAGE_PRODUCT]) {
      columns.push(
        columnHelper.menu({
          items: (
            <>
              <TableMenuItem onClick={onEditClick} data-cy="edit-product">
                {t("products.list.actions.edit")}
              </TableMenuItem>
              <TableMenuItem
                onClick={setProductToBeDelete}
                data-cy="delete-product"
                color="red.500"
              >
                {t("products.list.actions.delete")}
              </TableMenuItem>
            </>
          ),
        }),
      );
    }

    return columns;
  }, [onEditClick, permissions, t]);

  return (
    <QBodyLayout.Default>
      <QHeader>
        <QTitle>{t("products.list.title")}</QTitle>
        {!!permissions[UserPermissions.MANAGE_PRODUCT] && (
          <QButtonGroup>
            <QButton onClick={createProductClick} data-cy="create-product">
              {t("products.list.actions.create")}
            </QButton>
          </QButtonGroup>
        )}
      </QHeader>
      <Filtering.FilterProvider
        definitions={{}}
        searchTermKey={t("products.list.search")}
      >
        <DataProvider.FixedWithAutoPagination
          data={data}
          isLoading={isLoading && !data.length}
          filterOption={(item: Product, searchTerm: string | undefined) => {
            if (searchTerm) {
              return item.name
                .toLocaleLowerCase()
                .includes(searchTerm?.toLocaleLowerCase());
            }

            return true;
          }}
        >
          <QDataTable
            columns={columns}
            data-cy="product-list"
            getRowId={(product: Product) => product.id}
          >
            <Filtering.FormContent />
          </QDataTable>
        </DataProvider.FixedWithAutoPagination>
      </Filtering.FilterProvider>
      <>
        {productToDelete && (
          <QDeleteConfirmationModal
            inProgress={isDeleting}
            title={t("products.delete.modalTitle")}
            message={
              <QText fontSize="sm">
                {t("products.delete.confirmMessage", {
                  productName: productToDelete?.name,
                })}
              </QText>
            }
            onConfirm={() => {
              productToDelete &&
                deleteProduct(productToDelete.id, {
                  onSuccess: () => setProductToBeDelete(undefined),
                });
            }}
            onClose={() => setProductToBeDelete(undefined)}
          />
        )}
      </>
    </QBodyLayout.Default>
  );
};
